import { FormSkeleton } from "components/PortalLoading";
import React, { useState, useEffect } from "react";
import { FiGlobe, FiShield, FiPlus, FiTrash2, FiSave } from "react-icons/fi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import useBookGeoRestrictions from "api/BookResource/useBookGeoRestrictions";
import useUpdateBookGeoRestrictions from "api/BookResource/useUpdateBookGeoRestrictions";
import useCountries from "api/Admin/useCountries";

const GeoRestrictionManager = ({ bookId, bookTitle, onClose }) => {
	const { data: restrictions, isLoading: loadingRestrictions } = useBookGeoRestrictions(bookId);
	const { data: countries, isLoading: loadingCountries } = useCountries();
	const { mutateAsync: updateRestrictions, isLoading: updating } =
		useUpdateBookGeoRestrictions(bookId);

	const [geoRestricted, setGeoRestricted] = useState(false);
	const [restrictionMode, setRestrictionMode] = useState("whitelist");
	const [restrictionMessage, setRestrictionMessage] = useState("");
	const [selectedCountries, setSelectedCountries] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");

	// Initialize form with existing data
	useEffect(() => {
		if (restrictions?.data) {
			const data = restrictions.data;
			setGeoRestricted(data.geo_restricted || false);
			setRestrictionMode(data.geo_restriction_mode || "whitelist");
			setRestrictionMessage(data.geo_restriction_message || "");

			// Combine allowed and blocked countries
			const allCountries = [
				...(data.allowed_countries || []).map((country) => ({
					...country,
					restriction_type: "allowed",
				})),
				...(data.blocked_countries || []).map((country) => ({
					...country,
					restriction_type: "blocked",
				})),
			];
			setSelectedCountries(allCountries);
		}
	}, [restrictions]);

	const handleAddCountry = (country) => {
		const exists = selectedCountries.find((c) => c.country_code === country.code);
		if (!exists) {
			setSelectedCountries((prev) => [
				...prev,
				{
					country_code: country.code,
					country_name: country.name,
					restriction_type: restrictionMode === "whitelist" ? "allowed" : "blocked",
				},
			]);
		}
		setSearchTerm("");
	};

	const handleRemoveCountry = (countryCode) => {
		setSelectedCountries((prev) => prev.filter((c) => c.country_code !== countryCode));
	};

	const handleToggleRestrictionType = (countryCode) => {
		setSelectedCountries((prev) =>
			prev.map((country) =>
				country.country_code === countryCode
					? {
							...country,
							restriction_type: country.restriction_type === "allowed" ? "blocked" : "allowed",
					  }
					: country
			)
		);
	};

	const handleSave = async () => {
		try {
			await updateRestrictions({
				geo_restricted: geoRestricted,
				geo_restriction_mode: geoRestricted ? restrictionMode : null,
				geo_restriction_message: geoRestricted ? restrictionMessage : null,
				countries: geoRestricted ? selectedCountries : [],
			});
			onClose();
		} catch (error) {
			// Error is handled by the hook
		}
	};

	const filteredCountries =
		countries?.data?.filter(
			(country) =>
				country.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
				!selectedCountries.find((c) => c.country_code === country.code)
		) || [];

	const allowedCountries = selectedCountries.filter((c) => c.restriction_type === "allowed");
	const blockedCountries = selectedCountries.filter((c) => c.restriction_type === "blocked");

	if (loadingRestrictions || loadingCountries) {
		return (
			<FormSkeleton label="Loading book access policy and country settings…" />
		);
	}

	return (
		<div className="bg-white rounded-lg shadow-lg p-6">
			{/* Header */}
			<div className="flex items-center justify-between mb-6">
				<div className="flex items-center space-x-2">
					<FiGlobe className="w-5 h-5 text-blue-500" />
					<h2 className="text-xl font-bold text-gray-900">Geo-Restriction Settings</h2>
				</div>
				{onClose && (
					<button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
						×
					</button>
				)}
			</div>

			{/* Book Info */}
			<div className="bg-gray-50 p-3 rounded-lg mb-6">
				<h3 className="font-medium text-gray-900">{bookTitle}</h3>
				<p className="text-sm text-gray-600">Book ID: {bookId}</p>
			</div>

			{/* Enable/Disable Geo-Restriction */}
			<div className="mb-6">
				<label className="flex items-center space-x-3">
					<input
						type="checkbox"
						checked={geoRestricted}
						onChange={(e) => setGeoRestricted(e.target.checked)}
						className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
					/>
					<span className="text-sm font-medium text-gray-900">
						Enable geo-restriction for this book
					</span>
				</label>
			</div>

			{geoRestricted && (
				<>
					{/* Restriction Mode */}
					<div className="mb-6">
						<label className="block text-sm font-medium text-gray-700 mb-2">Restriction Mode</label>
						<div className="flex space-x-4">
							<label className="flex items-center space-x-2">
								<input
									type="radio"
									name="restriction_mode"
									value="whitelist"
									checked={restrictionMode === "whitelist"}
									onChange={(e) => setRestrictionMode(e.target.value)}
									className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
								/>
								<span className="text-sm text-gray-900">
									Whitelist (Allow only selected countries)
								</span>
							</label>
							<label className="flex items-center space-x-2">
								<input
									type="radio"
									name="restriction_mode"
									value="blacklist"
									checked={restrictionMode === "blacklist"}
									onChange={(e) => setRestrictionMode(e.target.value)}
									className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
								/>
								<span className="text-sm text-gray-900">Blacklist (Block selected countries)</span>
							</label>
						</div>
					</div>

					{/* Restriction Message */}
					<div className="mb-6">
						<label className="block text-sm font-medium text-gray-700 mb-2">
							Custom Restriction Message (Optional)
						</label>
						<textarea
							value={restrictionMessage}
							onChange={(e) => setRestrictionMessage(e.target.value)}
							rows={3}
							className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
							placeholder="Enter a custom message to show when access is restricted..."
						/>
					</div>

					{/* Add Countries */}
					<div className="mb-6">
						<label className="block text-sm font-medium text-gray-700 mb-2">Add Countries</label>
						<div className="relative">
							<input
								type="text"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								placeholder="Search and select countries..."
								className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
							/>
							{searchTerm && filteredCountries.length > 0 && (
								<div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 max-h-48 overflow-y-auto shadow-lg">
									{filteredCountries.map((country) => (
										<button
											key={country.code}
											onClick={() => handleAddCountry(country)}
											className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center space-x-2"
										>
											<FiPlus className="w-4 h-4 text-green-500" />
											<span>
												{country.name} ({country.code})
											</span>
										</button>
									))}
								</div>
							)}
						</div>
					</div>

					{/* Selected Countries */}
					{selectedCountries.length > 0 && (
						<div className="mb-6">
							<label className="block text-sm font-medium text-gray-700 mb-2">
								Selected Countries ({selectedCountries.length})
							</label>

							{/* Mode-specific sections */}
							{restrictionMode === "whitelist" && allowedCountries.length > 0 && (
								<div className="mb-4">
									<h4 className="text-sm font-medium text-green-700 mb-2 flex items-center">
										<FiShield className="w-4 h-4 mr-1" />
										Allowed Countries ({allowedCountries.length})
									</h4>
									<div className="space-y-2">
										{allowedCountries.map((country) => (
											<CountryItem
												key={country.country_code}
												country={country}
												onRemove={handleRemoveCountry}
												onToggle={handleToggleRestrictionType}
												mode={restrictionMode}
											/>
										))}
									</div>
								</div>
							)}

							{restrictionMode === "blacklist" && blockedCountries.length > 0 && (
								<div className="mb-4">
									<h4 className="text-sm font-medium text-red-700 mb-2 flex items-center">
										<FiShield className="w-4 h-4 mr-1" />
										Blocked Countries ({blockedCountries.length})
									</h4>
									<div className="space-y-2">
										{blockedCountries.map((country) => (
											<CountryItem
												key={country.country_code}
												country={country}
												onRemove={handleRemoveCountry}
												onToggle={handleToggleRestrictionType}
												mode={restrictionMode}
											/>
										))}
									</div>
								</div>
							)}

							{/* Mixed mode - show both types */}
							{restrictionMode === "whitelist" && blockedCountries.length > 0 && (
								<div className="mb-4">
									<h4 className="text-sm font-medium text-red-700 mb-2">
										Note: These countries are marked as blocked but will be ignored in whitelist
										mode
									</h4>
									<div className="space-y-2">
										{blockedCountries.map((country) => (
											<CountryItem
												key={country.country_code}
												country={country}
												onRemove={handleRemoveCountry}
												onToggle={handleToggleRestrictionType}
												mode={restrictionMode}
												dimmed
											/>
										))}
									</div>
								</div>
							)}

							{restrictionMode === "blacklist" && allowedCountries.length > 0 && (
								<div className="mb-4">
									<h4 className="text-sm font-medium text-green-700 mb-2">
										Note: These countries are marked as allowed but will be ignored in blacklist
										mode
									</h4>
									<div className="space-y-2">
										{allowedCountries.map((country) => (
											<CountryItem
												key={country.country_code}
												country={country}
												onRemove={handleRemoveCountry}
												onToggle={handleToggleRestrictionType}
												mode={restrictionMode}
												dimmed
											/>
										))}
									</div>
								</div>
							)}
						</div>
					)}
				</>
			)}

			{/* Save Button */}
			<div className="flex justify-end space-x-3">
				{onClose && (
					<button
						onClick={onClose}
						className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
					>
						Cancel
					</button>
				)}
				<button
					onClick={handleSave}
					disabled={updating}
					className="px-4 py-2 bg-akgreener text-white rounded-lg hover:bg-akgreen disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
				>
					{updating ? (
						<AiOutlineLoading3Quarters className="animate-spin w-4 h-4" />
					) : (
						<FiSave className="w-4 h-4" />
					)}
					<span>{updating ? "Saving..." : "Save Changes"}</span>
				</button>
			</div>
		</div>
	);
};

// Country Item Component
const CountryItem = ({ country, onRemove, onToggle, mode, dimmed = false }) => {
	const isAllowed = country.restriction_type === "allowed";
	const isActive = (mode === "whitelist" && isAllowed) || (mode === "blacklist" && !isAllowed);

	return (
		<div
			className={`flex items-center justify-between p-3 border rounded-lg ${
				dimmed
					? "opacity-50 bg-gray-50"
					: isActive
					? "bg-blue-50 border-blue-200"
					: "bg-red-50 border-red-200"
			}`}
		>
			<div className="flex items-center space-x-3">
				<div className={`w-3 h-3 rounded-full ${isAllowed ? "bg-green-500" : "bg-red-500"}`} />
				<span className="font-medium">{country.country_name}</span>
				<span className="text-sm text-gray-500">({country.country_code})</span>
			</div>
			<div className="flex items-center space-x-2">
				<button
					onClick={() => onToggle(country.country_code)}
					className={`px-2 py-1 text-xs rounded ${
						isAllowed
							? "bg-green-100 text-green-800 hover:bg-green-200"
							: "bg-red-100 text-red-800 hover:bg-red-200"
					} transition-colors`}
				>
					{isAllowed ? "Allowed" : "Blocked"}
				</button>
				<button
					onClick={() => onRemove(country.country_code)}
					className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
				>
					<FiTrash2 className="w-4 h-4" />
				</button>
			</div>
		</div>
	);
};

export default GeoRestrictionManager;
